/**
 * Die Shell der Monteur-App — `PWA-001`.
 *
 * `07` §7.7: vier Hauptbereiche — Heute, Einsätze, Dateien, Mehr. Die
 * Navigation liegt **unten**, nicht oben: Sie wird mit dem Daumen bedient, und
 * `07` §7.14 verlangt Bedienung mit einer Hand. Am oberen Rand eines
 * Sechs-Zoll-Displays kommt der Daumen nicht an, ohne das Gerät umzugreifen —
 * auf einer Leiter ist das keine Kleinigkeit.
 *
 * **Kein Rahmen um Fremdoberflächen** wie im Portal (ADR-040). Die App zeigt
 * nur eigene Ansichten; was aus Nextcloud oder Paperless kommt, erscheint als
 * Verweis mit Offlinekennzeichnung. Ein eingebetteter Fremddienst wäre auf
 * einem Handydisplay ohnehin unbrauchbar und im Funkloch leer.
 */

import { useEffect, useState } from "react";
import { NavLink, Outlet, Route, Routes, useLocation } from "react-router";

import { cssVariablen } from "@hwc/ui";
import type { Erscheinung } from "@hwc/ui";

import { MonteurRahmen, useMonteur, zurueckstellen } from "./Sitzung.js";
import { Heute } from "../features/day/Heute.js";
import { Einsatzdetail, Einsatzliste } from "../features/job/Einsatzdetail.js";
import { Dateien } from "../features/resources/Dateien.js";
import { Mehr } from "./Mehr.js";

/** Die vier Bereiche aus `07` §7.7, in der Reihenfolge des Dokuments. */
const BEREICHE = [
  { pfad: "/", text: "Heute" },
  { pfad: "/einsaetze", text: "Einsätze" },
  { pfad: "/dateien", text: "Dateien" },
  { pfad: "/mehr", text: "Mehr" },
] as const;

function Rahmen() {
  const { monteur, online, setzeOnline, lokal } = useMonteur();
  const ort = useLocation();

  // Wie im Portal: Bei clientseitigem Routing setzt der Browser keinen Titel.
  useEffect(() => {
    const treffer = BEREICHE.find((b) =>
      b.pfad === "/" ? ort.pathname === "/" : ort.pathname.startsWith(b.pfad),
    );
    document.title = treffer && treffer.pfad !== "/"
      ? `${treffer.text} — Monteur`
      : "Monteur";
  }, [ort.pathname]);

  return (
    <div className="app">
      <a className="app__sprung" href="#inhalt">
        Zum Inhalt springen
      </a>

      <header className="app__kopf">
        <span className="app__marke">Handwerker-Cloud</span>
        <span className="app__monteur">{monteur.kuerzel}</span>
        {/*
          Der Netzschalter. Er gehört in einen Demonstrationsstand und nicht in
          die ausgelieferte App — dort kommt der Zustand vom Gerät. Er steht
          hier, weil sich die Offline-UX aus `07` §7.9 sonst nicht beurteilen
          lässt: Man müsste das WLAN abschalten, und dann lädt auch der
          Entwicklungsserver nicht mehr.
        */}
        <button
          type="button"
          className={`app__netz app__netz--${online ? "an" : "aus"}`}
          onClick={() => setzeOnline(!online)}
          aria-pressed={!online}
        >
          {online ? "Online" : "Funkloch"}
          {lokal.warteschlange.length > 0 && ` · ${lokal.warteschlange.length}`}
        </button>
      </header>

      <main id="inhalt" className="app__inhalt" tabIndex={-1}>
        <Outlet />
      </main>

      <nav className="app__nav" aria-label="Hauptbereiche">
        {BEREICHE.map((b) => (
          <NavLink
            key={b.pfad}
            to={b.pfad}
            end={b.pfad === "/"}
            className={({ isActive }) =>
              isActive ? "app__navpunkt app__navpunkt--aktiv" : "app__navpunkt"
            }
          >
            {b.text}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * Die Erscheinung.
 *
 * Nur `hell` und `dunkel`, keine Automatik wie im Portal — und die Vorgabe ist
 * **hell**. Der Grund ist nicht Geschmack: Ein Monteur arbeitet überwiegend
 * draußen, und ein dunkles Bild im Sonnenlicht ist unlesbar, während ein
 * helles im Dunkeln blendet, aber lesbar bleibt. Wer nachts im Keller
 * arbeitet, schaltet um.
 */
function useErscheinung(): [Erscheinung, (e: Erscheinung) => void] {
  const [wahl, setzeWahl] = useState<Erscheinung>(() => {
    try {
      const gemerkt = window.localStorage.getItem("hwc.monteur.erscheinung");
      return gemerkt === "dunkel" ? "dunkel" : "hell";
    } catch {
      return "hell";
    }
  });

  const waehle = (neu: Erscheinung) => {
    setzeWahl(neu);
    try {
      window.localStorage.setItem("hwc.monteur.erscheinung", neu);
    } catch {
      // s. `speicher.ts`
    }
  };

  useEffect(() => {
    document.documentElement.dataset["erscheinung"] = wahl;
    document.documentElement.style.colorScheme = wahl === "dunkel" ? "dark" : "light";
  }, [wahl]);

  return [wahl, waehle];
}

export function App() {
  const [erscheinung, setzeErscheinung] = useErscheinung();

  return (
    <MonteurRahmen>
      {/* Die Token als CSS-Variablen — erzeugt, nicht abgeschrieben (ADR-040). */}
      <style>{`:root {\n${cssVariablen(erscheinung, {})}\n}`}</style>
      <Routes>
        <Route element={<Rahmen />}>
          <Route index element={<Heute />} />
          <Route path="einsaetze" element={<Einsatzliste />} />
          <Route path="einsatz/:kennung" element={<Einsatzdetail />} />
          <Route path="dateien" element={<Dateien />} />
          <Route
            path="mehr"
            element={
              <Mehr
                erscheinung={erscheinung}
                setzeErscheinung={setzeErscheinung}
                zuruecksetzen={zurueckstellen}
              />
            }
          />
          <Route
            path="*"
            element={
              <>
                <h1>Diese Ansicht gibt es nicht</h1>
                <p className="leise">
                  Der Link führt ins Leere. Über die vier Bereiche unten kommen Sie
                  überall hin.
                </p>
              </>
            }
          />
        </Route>
      </Routes>
    </MonteurRahmen>
  );
}
